<template>
  <div class="ds-page" v-if="!$store.getters.isForbidden">
    <!-- Page Header -->
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">WPS</div>
          <h2 class="page-title">Wage Payment System</h2>
          <p class="page-subtitle">Monitor and process WPS requests across all stages</p>
        </div>
      </div>
    </div>

    <!-- Tab Card -->
    <div class="ds-card">
      <b-tabs :value="current_active_index" @input="handleTabInput" class="wps-tabs">
        <b-tab :active-id="index.toString()" v-for="(opt, index) in tab_options" :key="index" :title="opt.name">
          <!-- Tab Header -->
          <div class="wps-tab-header">
            <span class="wps-tab-title">WPS {{ opt.name }}</span>
            <div class="wps-tab-actions">
              <button class="wps-refresh-btn" @click="getList()" title="Refresh">
                <i class="el-icon-refresh"></i>
              </button>
              <el-input
                clearable
                @clear="searchHandler"
                size="small"
                type="text"
                v-model="search_keyword"
                placeholder="Search company name..."
                style="width: 220px"
              />
              <button @click="searchHandler" class="ds-btn ds-btn--blue ds-btn--sm">
                <font-awesome-icon icon="search" />
                Search
              </button>
            </div>
          </div>

          <!-- Table: Standard WPS -->
          <div class="ds-table-wrap" v-if="!isInvoiceTab(opt.value)">
            <table class="ds-table" v-loading="is_loading">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Client</th>
                  <th class="text-right">Employees</th>
                  <th class="text-right">Total Amount</th>
                  <th class="text-right">Total Salary</th>
                  <th class="text-right">Admin Fee</th>
                  <th>Requested By</th>
                  <th>Disburse</th>
                  <th>Status</th>
                  <th class="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in list" :key="i">
                  <td>
                    <span class="ds-text-bold">{{ item.company?.company_name || "—" }}</span>
                  </td>
                  <td>{{ item.company_client?.name || "—" }}</td>
                  <td class="text-right">
                    <span class="ds-badge">{{ item.total_employee }}</span>
                  </td>
                  <td class="text-right">
                    <span class="ds-amount">{{ formatCurrency(item.total_amount) }}</span>
                  </td>
                  <td class="text-right">
                    <span class="ds-amount">{{ formatCurrency(item.total_salary) }}</span>
                  </td>
                  <td class="text-right">
                    <span class="ds-amount--muted">{{ formatCurrency(item.admin_fee) }}</span>
                  </td>
                  <td>
                    <div class="ds-user-cell">
                      <div class="ds-avatar" :class="dsAvatarVariant(item?.requested_by?.full_name)">
                        {{ dsGetInitials(item?.requested_by?.full_name) }}
                      </div>
                      <div>
                        <div class="ds-user-cell__name">{{ item?.requested_by?.full_name || "—" }}</div>
                        <div class="ds-user-cell__sub">{{ item?.requested_by?.email || "" }}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="ds-pill" :class="dsStatusClass(item.disburse_status)">
                      {{ dsStatusLabel(item.disburse_status) }}
                    </span>
                  </td>
                  <td>
                    <span class="ds-pill" :class="dsStatusClass(item.status)">
                      {{ dsStatusLabel(item.status) }}
                    </span>
                  </td>
                  <td class="text-center">
                    <div class="ds-row-actions">
                      <el-tooltip v-if="canTakeFinanceAction(opt.value)" content="Inquiry WPS" placement="top">
                        <el-button
                          round
                          @click="handlerBtnApprove(item, opt.value)"
                          type="success"
                          size="mini"
                          class="wps-action-approve"
                          circle
                        >
                          <font-awesome-icon icon="check" />
                        </el-button>
                      </el-tooltip>
                      <el-tooltip v-if="canReadFinanceWps" content="View request detail" placement="top">
                        <el-button
                          round
                          size="mini"
                          class="wps-action-view"
                          circle
                          @click="handlerBtnView(item.id, opt.value, opt.value === 'WAITING_APPROVAL' ? 'APPROVED' : null)"
                        >
                          <font-awesome-icon icon="address-book" />
                        </el-button>
                      </el-tooltip>
                      <el-tooltip v-if="canViewInvoices(opt.value)" content="View invoice list" placement="top">
                        <el-button
                          round
                          size="mini"
                          type="primary"
                          class="wps-action-invoice"
                          circle
                          @click="handlerBtnViewInvoice(item.company_id, item.company?.company_name || item.company?.name || '—')"
                        >
                          <font-awesome-icon icon="file-invoice" />
                        </el-button>
                      </el-tooltip>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Table: Invoice Tab -->
          <div class="ds-table-wrap" v-else>
            <table class="ds-table" v-loading="is_loading">
              <thead>
                <tr>
                  <th>Company</th>
                  <th class="text-right">Total Invoice</th>
                  <th class="text-right">Total Clients</th>
                  <th class="text-right">Total Amount</th>
                  <th class="text-right">Admin Fee</th>
                  <th class="text-center">Draft</th>
                  <th class="text-center">Paid</th>
                  <th class="text-center">Overdue</th>
                  <th class="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in list" :key="i">
                  <td>
                    <span class="ds-text-bold">{{ item.company_name }}</span>
                  </td>
                  <td class="text-right">
                    <span class="ds-badge">{{ item.total_invoices }}</span>
                  </td>
                  <td class="text-right">
                    <span class="ds-badge">{{ item.total_company_clients }}</span>
                  </td>
                  <td class="text-right">
                    <span class="ds-amount">{{ formatCurrency(item.total_amount) }}</span>
                  </td>
                  <td class="text-right">
                    <span class="ds-amount--muted">{{ formatCurrency(item.total_admin_fee) }}</span>
                  </td>
                  <td class="text-center">
                    <span class="ds-pill ds-pill--gray">{{ item.draft_count }}</span>
                  </td>
                  <td class="text-center">
                    <span class="ds-pill ds-pill--success">{{ item.paid_count }}</span>
                  </td>
                  <td class="text-center">
                    <span class="ds-pill ds-pill--danger">{{ item.overdue_count }}</span>
                  </td>
                  <td class="text-center">
                    <div class="ds-row-actions">
                      <el-tooltip v-if="canViewInvoices(opt.value)" content="View invoice list" placement="top">
                        <el-button
                          round
                          size="mini"
                          type="primary"
                          class="wps-action-invoice"
                          circle
                          @click="handlerBtnViewInvoice(item.company_id, item.company_name)"
                        >
                          <font-awesome-icon icon="file-invoice" />
                        </el-button>
                      </el-tooltip>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <el-empty
            :description="isInvoiceTab(opt.value) ? 'No WPS invoice for now' : 'No WPS request for now'"
            v-if="!total_rows"
            :image="emptyStateImage"
            class="ds-empty"
          />

          <pagination :page="page" :per_page="per_page" :list="list" :total_rows="total_rows" @pageChange="pageChangeHandler" />
        </b-tab>
      </b-tabs>
    </div>

    <!-- Approve Modal -->
    <b-modal size="md" v-model="show.modal_wps" :title="approvalModalTitle" hide-footer>
      <div class="wps-modal-body">
        <div v-if="shouldDisplayDisburseProcessForm && disburse.auto_disburse" class="wps-alert wps-alert--success">
          <font-awesome-icon icon="info-circle" class="wps-alert__icon" />
          <span><strong>Disbursed by system</strong> is activated</span>
        </div>
        <div v-else-if="shouldDisplayDisburseProcessForm" class="wps-alert wps-alert--warning">
          <font-awesome-icon icon="info-circle" class="wps-alert__icon" />
          <span><strong>Disbursement by system</strong> is deactivated</span>
        </div>

        <el-descriptions :loading="loading.modal_wps" title="" :column="1" border>
          <el-descriptions-item>
            <template slot="label"> <i class="el-icon-office-building"></i> Request Number </template>
            <strong>{{ selected_wps.request_number }}</strong>
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <font-awesome-icon icon="users" size="sm" /> Total Recipients </template>
            {{ selected_wps.total_employee }} Employees
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <font-awesome-icon icon="money-bill-alt" size="sm" /> Total WPS Amount </template>
            <strong>{{ formatCurrency(selected_wps.total_amount) }}</strong>
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <font-awesome-icon icon="sticky-note" size="sm" /> Notes </template>
            <el-input v-model="disburse.notes" placeholder="Notes" type="text" size="small" />
          </el-descriptions-item>
          <el-descriptions-item v-if="shouldDisplayDisburseProcessForm && !disburse.auto_disburse">
            <template slot="label"> <font-awesome-icon icon="hashtag" size="sm" /> Reference Number </template>
            <el-input v-model="disburse.reference_number" placeholder="Input reference number" type="text" size="small" />
          </el-descriptions-item>
          <el-descriptions-item v-if="shouldDisplayDisburseProcessForm && !disburse.auto_disburse">
            <template slot="label"> <font-awesome-icon icon="file-upload" size="sm" /> Payment Confirmation </template>
            <b-form-file
              v-model="selected_file.payment_confirm_file"
              :state="Boolean(selected_file.payment_confirm_file)"
              class="mt-1"
              accept=".png, .jpg, .jpeg"
              @input="onChangeFile($event, 'payment_confirm_file')"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
            />
          </el-descriptions-item>
        </el-descriptions>

        <div class="wps-modal-actions">
          <button @click="show.modal_wps = false" class="ds-btn ds-btn--secondary ds-btn--sm">Cancel</button>
          <button @click="submitApproval" class="ds-btn ds-btn--primary ds-btn--sm">
            {{ approvalButtonText }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { designSystemMixin } from "@/mixins/designSystemMixin";
import {
  GET_WPS_LIST,
  INQUIRY_APPROVAL_WPS,
  SUBMIT_APPROVAL_FINANCE_WPS,
  PROCESS_DISBURSE_WPS,
  WPS_INVOICE_LIST,
} from "@/store/modules/wps";

import emptyStateImage from "@/assets/images/empty-state.png";
import popupErrorMessages from "@/library/popup-error-messages";
import Pagination from "../components/Pagination.vue";
import base64File from "@/library/base64File";

const DEFAULT_TAB = "WAITING_APPROVAL_FINANCE";
const TAB_SLUG_MAP = {
  WAITING_APPROVAL_FINANCE: "waiting-approval-finance",
  WAITING_DISBURSEMENT: "waiting-disbursement",
  PROCESS_DISBURSEMENT: "processing-disbursement",
  COMPLETED: "completed",
};
const SLUG_TAB_MAP = Object.entries(TAB_SLUG_MAP).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

export default {
  name: "WPS",
  mixins: [designSystemMixin],
  components: {
    Pagination,
  },
  metaInfo: {
    title: "Wage Payment System",
  },
  computed: {
    currentRole() {
      return this.$store.getters.current_role || {};
    },
    userProfile() {
      return this.$store.getters.currentMyProfile || {};
    },
    isMaker() {
      return Boolean(this.userProfile?.is_maker || this.currentRole?.page_name === "*");
    },
    isChecker() {
      return Boolean(this.userProfile?.is_checker || this.currentRole?.page_name === "*");
    },
    canReadFinanceWps() {
      return Boolean(this.currentRole.can_read || this.currentRole.page_name === "*");
    },
    canManageFinanceWps() {
      return Boolean(this.currentRole.can_edit || this.currentRole.page_name === "*");
    },
    approvalButtonText() {
      if (this.selected_wps.current_state === "WAITING_DISBURSEMENT") {
        return "Process Disbursement";
      }
      return "Submit Finance";
    },
    approvalModalTitle() {
      if (this.selected_wps.current_state === "WAITING_DISBURSEMENT") {
        return "Process Disbursement";
      }
      return "Approve WPS";
    },
    shouldDisplayDisburseProcessForm() {
      return this.selected_wps.current_state === "WAITING_DISBURSEMENT";
    },
  },
  data() {
    return {
      selected_tab: "WAITING_APPROVAL_FINANCE",
      search_keyword: "",
      emptyStateImage,
      disburse: {
        notes: "",
        auto_disburse: false,
        reference_number: "",
      },
      wps_approval_id: "",
      is_loading: false,
      total_rows: 0,
      page: 1,
      per_page: 10,
      list: [],
      show: {
        modal_wps: false,
      },
      loading: {
        modal_wps: false,
      },
      selected_wps: {},
      selected_file: {
        payment_confirm_file: null,
        payment_confirm_file_base64: null,
      },
      status_filter: "WAITING_APPROVAL",
      disburse_status_filter: "WAITING_APPROVAL",
      current_active_index: 0,
      tab_options: [
        {
          name: "Waiting Approval Finance",
          value: "WAITING_APPROVAL_FINANCE",
        },
        {
          name: "Waiting Disbursement",
          value: "WAITING_DISBURSEMENT",
        },
        {
          name: "Processing Disbursement",
          value: "PROCESS_DISBURSEMENT",
        },
        {
          name: "Completed",
          value: "COMPLETED",
        },
      ],
    };
  },
  methods: {
    getApiNamespace() {
      return "wage-payment-system";
    },
    formatCurrency(value) {
      return `Rp. ${Number(value || 0).toLocaleString("id-ID")}`;
    },
    canApproveFinanceRequest() {
      return this.canManageFinanceWps && this.isMaker;
    },
    canProcessFinanceDisbursement() {
      return this.canManageFinanceWps && this.isChecker;
    },
    canTakeFinanceAction(tab) {
      if (tab === "WAITING_APPROVAL_FINANCE") {
        return this.canApproveFinanceRequest();
      }

      if (["WAITING_DISBURSEMENT", "PROCESS_DISBURSEMENT"].includes(tab)) {
        return this.canProcessFinanceDisbursement();
      }

      return false;
    },
    canViewInvoices(tab) {
      return this.canReadFinanceWps && this.isInvoiceTab(tab);
    },
    isInvoiceTab(tab) {
      return tab === "COMPLETED";
    },
    getApiCurrentState(uiState) {
      const map = {
        WAITING_APPROVAL_FINANCE: "WAITING_APPROVAL",
        WAITING_DISBURSEMENT: "WAITING_DISBURSEMENT",
        PROCESS_DISBURSEMENT: "PROCESS_DISBURSEMENT",
        COMPLETED: "APPROVED",
      };
      return map[uiState] || uiState;
    },
    mapTabToFilters(tab) {
      switch (tab) {
        case "WAITING_APPROVAL_FINANCE":
          this.status_filter = "APPROVED";
          this.disburse_status_filter = "WAITING_APPROVAL";
          break;
        case "WAITING_DISBURSEMENT":
          this.status_filter = "APPROVED";
          this.disburse_status_filter = "WAITING_DISBURSEMENT";
          break;
        case "PROCESS_DISBURSEMENT":
          this.status_filter = "APPROVED";
          this.disburse_status_filter = "PROCESS_DISBURSEMENT";
          break;
        case "COMPLETED":
          this.status_filter = "APPROVED";
          this.disburse_status_filter = "COMPLETED";
          break;
        default:
          this.status_filter = "APPROVED";
          this.disburse_status_filter = "WAITING_APPROVAL";
          break;
      }
      this.current_active_index = this.tab_options.findIndex((v) => v.value === tab);
    },
    resolveTabSlug(tab) {
      return TAB_SLUG_MAP[tab] || TAB_SLUG_MAP[DEFAULT_TAB];
    },
    resolveTabValue(slug) {
      return SLUG_TAB_MAP[slug] || DEFAULT_TAB;
    },
    searchHandler() {
      this.page = 1;
      this.getList();
    },
    handleTabInput(index) {
      const tabValue = this.tab_options[index]?.value;
      if (!tabValue || tabValue === this.selected_tab) {
        return;
      }

      this.$router.replace({
        name: "Wage Payment System",
        params: { tab: this.resolveTabSlug(tabValue) },
      });
    },
    resetApprovalState() {
      this.wps_approval_id = "";
      this.disburse = {
        notes: "",
        auto_disburse: false,
        reference_number: "",
      };
      this.selected_file = {
        payment_confirm_file: null,
        payment_confirm_file_base64: null,
      };
    },
    async pageChangeHandler(page) {
      this.page = page;
      this.is_loading = true;
      await this.getList();
      this.is_loading = false;
    },
    async getList() {
      this.$parent.showLoader();
      try {
        const searchParams = this.search_keyword
          ? {
              search: "company_name",
              search_value: this.search_keyword,
            }
          : {};

        if (this.isInvoiceTab(this.selected_tab)) {
          const response = await this.$store.dispatch(WPS_INVOICE_LIST, {
            api_namespace: this.getApiNamespace(),
            page: this.page,
            size: this.per_page,
            per_page: this.per_page,
            ...searchParams,
          });
          const rows = Array.isArray(response?.rows) ? response.rows : [];
          this.total_rows = response?.count || 0;
          this.list = rows.map((item) => {
            const summary = item.summary || {};
            return {
              company_id: item.company_id,
              company_name: item.company?.company_name || "-",
              total_invoices: summary.total_invoices || 0,
              total_company_clients: summary.total_company_clients || 0,
              total_amount: summary.total_amount || 0,
              total_admin_fee: summary.total_admin_fee || 0,
              draft_count: summary.draft_count || 0,
              paid_count: summary.paid_count || 0,
              overdue_count: summary.overdue_count || 0,
            };
          });
          return;
        }

        await this.$store.dispatch(GET_WPS_LIST, {
          api_namespace: this.getApiNamespace(),
          page: this.page,
          per_page: this.per_page,
          ...searchParams,
          status: this.status_filter,
          disburse_status: this.disburse_status_filter,
        });

        const { rows = [], count = 0 } = this.$store.getters.wps_list || {};
        this.total_rows = count;
        this.list = Array.isArray(rows) ? rows : [];
      } finally {
        this.$parent.hideLoader();
        this.is_loading = false;
      }
    },
    handlerBtnApprove(item, current_state) {
      this.show.modal_wps = true;
      this.selected_wps = item;
      this.selected_wps.current_state = current_state;
      this.resetApprovalState();
      this.inquiry();
    },
    async inquiry() {
      this.loading.modal_wps = true;
      const payload = {
        id: this.selected_wps.id,
        api_namespace: this.getApiNamespace(),
        current_state: this.getApiCurrentState(this.selected_wps.current_state),
        approval_type: "batch",
        bank_code: [],
      };
      try {
        const response = await this.$store.dispatch(INQUIRY_APPROVAL_WPS, payload);
        const inquiryResult = response?.data?.data?.rows || {};

        this.disburse.auto_disburse = Boolean(inquiryResult.auto_disburse ?? inquiryResult.wpsDisburseAutomation);
        this.selected_wps.wps_approval_id = response?.data?.wps_approval_id || inquiryResult.wps_approval_id;
        this.wps_approval_id = response?.data?.wps_approval_id || inquiryResult.wps_approval_id || "";
      } catch (error) {
        console.error("Error during inquiry:", error);
      } finally {
        this.loading.modal_wps = false;
      }
    },
    async onChangeFile(event, target) {
      if (!this.selected_file[target]) {
        this.selected_file[`${target}_base64`] = null;
        return;
      }

      this.selected_file[`${target}_base64`] = await base64File(this.selected_file[target]);
    },
    async submitApproval() {
      const isFinanceApproval = this.selected_wps.current_state === "WAITING_APPROVAL_FINANCE";
      const isDisburseProcess = this.selected_wps.current_state === "WAITING_DISBURSEMENT";

      if (!this.canTakeFinanceAction(this.selected_wps.current_state)) {
        this.$message.error("You do not have permission to perform this action.");
        return;
      }

      let confirmMessage = "You will approve this WPS batch. You cannot undo this action. Continue?";
      if (isFinanceApproval) {
        confirmMessage = "You will approve this WPS disbursement batch. You cannot undo this action. Continue?";
      } else if (isDisburseProcess) {
        confirmMessage = "You will process this WPS disbursement batch. You cannot undo this action. Continue?";
      }

      if (isDisburseProcess && !this.disburse.auto_disburse) {
        if (!this.disburse.reference_number) {
          this.$message.error("Reference number is required.");
          return;
        }
        if (!this.selected_file.payment_confirm_file_base64) {
          this.$message.error("Payment confirmation file is required.");
          return;
        }
      }

      this.$confirm(confirmMessage, "Confirmation", {
        type: "info",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            try {
              const payload = {
                id: this.selected_wps.id,
                api_namespace: this.getApiNamespace(),
                notes: this.disburse.notes,
                wps_approval_id: this.wps_approval_id,
              };
              if (isDisburseProcess) {
                payload.reference_number = this.disburse.reference_number;
                if (!this.disburse.auto_disburse) {
                  payload.payment_confirm_file = this.selected_file.payment_confirm_file_base64;
                }
                await this.$store.dispatch(PROCESS_DISBURSE_WPS, payload);
              } else if (isFinanceApproval) {
                await this.$store.dispatch(SUBMIT_APPROVAL_FINANCE_WPS, payload);
              }
              this.$message({
                title: "Success",
                type: "success",
                message: "Approval success",
              });
              this.show.modal_wps = false;
              this.getList();
            } catch (err) {
              if (err.response?.data) {
                popupErrorMessages(err.response.data);
              }
            }

            instance.confirmButtonLoading = false;
          }
          done();
        },
      }).catch(() => {});
    },
    async handlerBtnView(id, status, approval_status) {
      const request = this.list.find((item) => item.id === id);
      this.$router.push({
        name: "Wage Payment System Detail",
        params: {
          id,
          status,
        },
        query: {
          approval_status,
          request_number: request?.request_number || "",
        },
      });
    },
    handlerBtnViewInvoice(company_id, company_name) {
      this.$router.push({
        name: "Wage Payment System Invoice",
        params: {
          id: company_id,
        },
        query: {
          company_name,
        },
      });
    },
  },
  watch: {
    "$route.params.tab": {
      immediate: true,
      async handler(tabSlug) {
        const selectedTab = this.resolveTabValue(tabSlug);
        this.selected_tab = selectedTab;
        this.mapTabToFilters(selectedTab);
        this.page = 1;
        this.list = [];
        this.total_rows = 0;
        this.is_loading = true;
        await this.getList();
        this.is_loading = false;
      },
    },
  },
};
</script>

<style scoped>
.wps-tab-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(101, 109, 119, 0.16);
}
.wps-tab-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #232e3c;
  flex-shrink: 0;
}
.wps-tab-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.wps-action-approve.el-button,
.wps-action-view.el-button {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  font-size: 11px !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}
.wps-action-approve.el-button--success {
  background: rgba(47, 179, 68, 0.1) !important;
  border-color: rgba(47, 179, 68, 0.24) !important;
  color: #2fb344 !important;
}
.wps-action-approve.el-button--success:hover {
  background: rgba(47, 179, 68, 0.2) !important;
}
.wps-action-view.el-button {
  background: #f4f6fa !important;
  border-color: rgba(101, 109, 119, 0.24) !important;
  color: #656d77 !important;
}
.wps-action-view.el-button:hover {
  background: rgba(101, 109, 119, 0.1) !important;
}

/* ── Modal ────────────────────────────────────────────────── */
.wps-modal-body {
  padding: 4px 0;
}
.wps-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}
</style>
